"use client";

import React from "react";
import { ImSpinner2 } from "react-icons/im";
import {
  SimpleAppleIcon,
  SimpleFacebookIcon,
  SimpleGoogleIcon,
} from "../AuthIcons";

type Provider = "google" | "facebook" | "apple";

export interface AuthProviderButtonProps {
  provider: Provider;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  fullWidth?: boolean;
  iconSize?: number;
  className?: string;
}

const baseClasses =
  "inline-flex items-center justify-center h-11 px-4 rounded-lg text-sm font-medium focus-visible:outline-none focus-visible:ring-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

const variantClasses: Record<Provider, string> = {
  google:
    "bg-white border border-[#DADCE0] text-[#3C4043] focus-visible:ring-[#1A73E8]",
  facebook: "bg-[#1877F2] text-white focus-visible:ring-[#1877F2]",
  apple: "bg-black text-white focus-visible:ring-black",
};

const hoverClasses: Record<Provider, string> = {
  google: "hover:bg-[#F8F9FA]",
  facebook: "hover:bg-[#166FE5]",
  apple: "hover:bg-black/90",
};

const IconByProvider: Record<
  Provider,
  React.FC<{ size?: number; className?: string }>
> = {
  google: SimpleGoogleIcon,
  facebook: SimpleFacebookIcon,
  apple: SimpleAppleIcon,
};

export function AuthProviderButton({
  provider,
  label,
  onClick,
  disabled = false,
  isLoading = false,
  fullWidth = true,
  iconSize = 20,
  className = "",
}: AuthProviderButtonProps) {
  const Icon = IconByProvider[provider];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isLoading}
      aria-label={label}
      aria-busy={isLoading}
      className={`${baseClasses} ${variantClasses[provider]} ${hoverClasses[provider]} ${
        fullWidth ? "w-full" : ""
      } ${className}`}
    >
      {isLoading ? (
        <span className="flex items-center">
          <ImSpinner2 className="mr-3 h-4 w-4 animate-spin" />
          {label}
        </span>
      ) : (
        <>
          <Icon size={iconSize} className="mr-3" />
          <span>{label}</span>
        </>
      )}
    </button>
  );
}

export default AuthProviderButton;
